import { cloneDeep } from 'lodash-es';
import { type JSX, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { Switch } from 'Components/Buttons/Switch';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FileManagementArea, useFileManagementArea } from 'Components/Files/FileManagementArea';
import { ConfirmationModal } from 'Components/Modal/ConfirmationModal';
import { ModalHeader } from 'Components/Modal/ModalHeader';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SUBMIT } from 'Config/Icons';
import { iso8601ToUsDateLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AiTprmServiceAssessment, AiTprmServiceAssessmentRequest } from 'Models/ArtificialIntelligence';
import { ValidationError } from 'Models/ErrorTypes';
import { FileUpdates } from 'Models/Files';

import styles from './aiTprmServiceAssessmentModal.module.css';

enum FormState {
    None,
    Submitting,
    Submitted,
}

interface AdvertiseAiTprmServiceAssessmentModalProps {
    hideModal: () => void;
}

/**
 * This informational modal is used when the Client does not have License.AI_TPRM_SERVICE_ASSESSMENT. It acts as an advertisement for the feature.
 */
export const AdvertiseAiTprmServiceAssessmentModal = (props: AdvertiseAiTprmServiceAssessmentModalProps): JSX.Element => {
    return (
        <Modal show onHide={props.hideModal} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Body className={'modalFromBody'}>
                <ModalHeader text="SummIT Guide™" />
                <Text>Your organization is not licensed for SummIT Guide™. Please contact support@highpeakssolutions.com for assistance.</Text>
                <Text>SummIT Guide™ provides an automated assessment of responses to the Third-Party Questionnaire. Suggested control effectiveness ratings and corresponding explanations are populated in the "Additional Information" field for each control in the Control Assessment for your review.</Text>
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

interface ConfirmAiTprmServiceAssessmentModalProps {
    aiTprmServiceAssessment?: AiTprmServiceAssessment;
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    hideModal: () => void;
    thirdPartyId: string;
    thirdPartyServiceId: string;
}

/** This confirmation modal is used when the Client does have License.AI_TPRM_SERVICE_ASSESSMENT. It describes what will happen if the user confirms the action and lays out a disclaimer for the output. */
export const ConfirmAiTprmServiceAssessmentModal = (props: ConfirmAiTprmServiceAssessmentModalProps): JSX.Element => {
    const [submitRequestWithManagedFiles, fileManagementHookValues] = useFileManagementArea(props.documentApi, props.aiTprmServiceAssessment?.files ?? []);
    const cachedData = useCachedData();

    const [formState, setFormState] = useState(FormState.None);
    const [includeAssessmentWideFiles, setIncludeAssessmentWideFiles] = useState<boolean>(true);
    const [includeClientResponses, setIncludeClientResponses] = useState<boolean>(props.aiTprmServiceAssessment ? props.aiTprmServiceAssessment.include_client_responses : false);
    const [includeThirdPartyResponses, setIncludeThirdPartyResponses] = useState<boolean>(props.aiTprmServiceAssessment ? props.aiTprmServiceAssessment.include_third_party_responses : true);

    const submit = async (): Promise<string> => {
        setFormState(FormState.Submitting);

        await submitRequestWithManagedFiles(async (fileUpdates) => {
            // If the user toggles off the button to include assessment-wide files, ensure files that were previously uploaded are deleted and files that may have been added to the file management area are not included in the request.
            const fileUpdatesToSubmit = cloneDeep(fileUpdates);
            if (includeAssessmentWideFiles === false) {
                if (props.aiTprmServiceAssessment?.files) {
                    for (const file of props.aiTprmServiceAssessment.files) {
                        fileUpdatesToSubmit.existing_files_to_delete.push(file.file_id);
                    }
                }
                fileUpdatesToSubmit.new_files = [];
            }

            validateInput(fileUpdatesToSubmit);

            const aiTprmServiceAssessmentRequest: AiTprmServiceAssessmentRequest = {
                include_client_responses: includeClientResponses,
                include_third_party_responses: includeThirdPartyResponses,
                file_updates: fileUpdatesToSubmit,
            };
            await props.artificialIntelligenceApi.aiTprmServiceAssessmentStart(aiTprmServiceAssessmentRequest, props.thirdPartyId, props.thirdPartyServiceId);
        });

        setFormState(FormState.Submitted);

        return 'Control assessment suggestions are being created.';
    };

    /** Validate the input before submitting to the API. */
    const validateInput = (fileUpdates: FileUpdates) => {
        let fileCount: number;
        if (includeAssessmentWideFiles === false) {
            fileCount = 0;
        } else {
            fileCount = (props.aiTprmServiceAssessment ? props.aiTprmServiceAssessment.files.length : 0) - fileUpdates.existing_files_to_delete.length + fileUpdates.new_files.length;
        }
        if (includeClientResponses === false && includeThirdPartyResponses === false && fileCount === 0) {
            throw new ValidationError('Third-party questionnaire responses, control assessment responses, or assessment-wide files must be submitted.');
        }
    };

    const disableForm = formState === FormState.Submitting || formState === FormState.Submitted;

    return (
        <ConfirmationModal buttonText="Create" buttonLoadingText="Creating..." buttonIcon={ICON_SUBMIT} operationType="nonDestructive" headerText="SummIT Guide™ Control Assessment Suggestions" areYouSureText={`Are you sure you want SummIT Guide™ to create control assessment suggestions?`} performOperation={submit} hideModal={props.hideModal}>
            <Text>SummIT Guide™ provides an automated assessment of responses to the Third-Party Questionnaire. Suggested control effectiveness ratings and corresponding explanations are populated in the "Additional Information" field for each control in the Control Assessment for your review. This may take several minutes to complete, depending on the number and complexity of responses.</Text>
            {props.aiTprmServiceAssessment && (
                <Text>
                    Last executed {iso8601ToUsDateLong(props.aiTprmServiceAssessment.created_timestamp)} by {getUserNameFromSubject(props.aiTprmServiceAssessment.created_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}.
                </Text>
            )}
            <div className={styles.toggleButtonsContainer}>
                <div className={styles.toggleButton}>
                    <Switch checked={includeThirdPartyResponses} disabled={disableForm} onChange={() => setIncludeThirdPartyResponses(!includeThirdPartyResponses)} />
                    <Text noStyles>Include "Third-Party Questionnaire" responses. This includes information submitted by the third party in the analysis performed by SummIT Guide™.</Text>
                </div>
                <div className={styles.toggleButton}>
                    <Switch checked={includeClientResponses} disabled={disableForm} onChange={() => setIncludeClientResponses(!includeClientResponses)} />
                    <Text noStyles>Include "Control Assessment" responses. This includes information submitted by you or another user of your organization in the analysis performed by SummIT Guide™.</Text>
                </div>
                <div className={styles.toggleButton}>
                    <Switch checked={includeAssessmentWideFiles} disabled={disableForm} onChange={() => setIncludeAssessmentWideFiles(!includeAssessmentWideFiles)} />
                    <Text noStyles>Include assessment-wide files that are applied to every control in the assessment during the analysis by SummIT Guide™.</Text>
                </div>
            </div>
            {includeAssessmentWideFiles && <FileManagementArea disabled={formState === FormState.Submitting || formState === FormState.Submitted} documentApi={props.documentApi} fileManagementHookValues={fileManagementHookValues} />}
            <br />
            <GenerativeAiDisclaimer />
        </ConfirmationModal>
    );
};
